import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { KpiValue } from '@teamsystem-impact/shared';
import { reportApi } from '../api/report.api';
import { KPI_SECTIONS, getAllKpiCodes } from '../kpiConfig';
import { KpiSection } from './KpiSection';
import { KpiSourcePopover } from './KpiSourcePopover';

interface KpiDashboardTabProps {
  campaignId: string;
  campaignName: string;
  onOpenDatapoint?: (code: string) => void;
}

export function KpiDashboardTab({ campaignId, campaignName, onOpenDatapoint }: KpiDashboardTabProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Map<string, KpiValue>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [popover, setPopover] = useState<{ anchorEl: HTMLElement; code: string } | null>(null);

  const codes = useMemo(() => getAllKpiCodes(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await reportApi.getKpis(campaignId, codes);
        if (cancelled) return;
        const map = new Map<string, KpiValue>(response.kpis.map((k) => [k.code, k]));
        setValues(map);
      } catch (err: any) {
        if (!cancelled) setError(err.message || t('kpiDashboard.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // `t` è escluso intenzionalmente dalle deps: cambia referenza ad ogni render,
    // ri-fetcharebbe infinite volte. Il fallback label è usato solo se err.message
    // è vuoto, caso raro; l'utente vede il testo tecnico di errore in entrambe le lingue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, codes]);

  const popoverKpi = popover ? values.get(popover.code) : null;
  const popoverDef = popover
    ? KPI_SECTIONS.flatMap((s) => [
        ...(s.kpis ?? []),
        ...(s.subsections?.flatMap((sub) => sub.kpis) ?? []),
      ]).find((k) => k.code === popover.code)
    : null;

  const handleCodeClick = onOpenDatapoint
    ? (code: string) => {
        setPopover(null);
        onOpenDatapoint(code);
      }
    : undefined;

  return (
    <Box>
      <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">{t('kpiDashboard.campaignLabel')}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>{campaignName}</Typography>
        <Typography variant="caption" sx={{ ml: 'auto', color: '#aaa' }}>
          {t('kpiDashboard.clickHint')}
        </Typography>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && KPI_SECTIONS.map((section) => (
        <KpiSection
          key={section.id}
          section={section}
          values={values}
          onKpiClick={(code, anchorEl) => setPopover({ code, anchorEl })}
        />
      ))}

      {popoverKpi && (
        <KpiSourcePopover
          anchorEl={popover!.anchorEl}
          open={!!popover}
          onClose={() => setPopover(null)}
          kpi={{ ...popoverKpi, label: popoverDef?.label }}
          campaignName={campaignName}
          onCodeClick={handleCodeClick}
        />
      )}
    </Box>
  );
}
